import { getLocalTimeZone, today } from '@internationalized/date';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import type { ComponentProps } from 'react';
import {
  Button,
  CalendarCell as CalendarCellRac,
  CalendarGridBody as CalendarGridBodyRac,
  CalendarGridHeader as CalendarGridHeaderRac,
  CalendarGrid as CalendarGridRac,
  CalendarHeaderCell as CalendarHeaderCellRac,
  Calendar as CalendarRac,
  RangeCalendar as RangeCalendarRac,
  composeRenderProps,
} from 'react-aria-components';
import { Button as DesignButton } from './button';
import { cn } from './helpers';
import { MonthDropdown } from './months-dropdown';
import { YearDropdown } from './years-dropdown';
import { PopoverClose } from './popover';

interface BaseCalendarProps {
  className?: string;
}

/** Ok / Cancel under the month grid — opt-in via `showActions`. */
type CalendarActionsProps = {
  showActions?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmDisabled?: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
};

/**
 * Local prop name — react-aria also exports `CalendarProps<T>`.
 * Actions are listed on the object (not only via intersection) so callers
 * always see `showActions` in the public type.
 */
type DesignCalendarProps = ComponentProps<typeof CalendarRac> &
  BaseCalendarProps & {
    closeOnSelect?: boolean;
    showActions?: boolean;
    confirmLabel?: string;
    cancelLabel?: string;
    confirmDisabled?: boolean;
    onConfirm?: () => void;
    onCancel?: () => void;
  };

type DesignRangeCalendarProps = ComponentProps<typeof RangeCalendarRac> &
  BaseCalendarProps & {
    closeOnSelect?: boolean;
    showActions?: boolean;
    confirmLabel?: string;
    cancelLabel?: string;
    confirmDisabled?: boolean;
    onConfirm?: () => void;
    onCancel?: () => void;
  };

const cheveronStyle =
  'text-gray-900/80 cursor-pointer hover:bg-primary-100 hover:text-gray-900 focus-visible:ring-ring/50 flex size-9 items-center justify-center rounded-md transition-[color,box-shadow] outline-none focus-visible:ring-[3px]';

function CalendarHeader() {
  return (
    <header className='flex w-full items-center justify-between gap-1 pb-1'>
      <div className='flex items-center gap-1'>
        <MonthDropdown />
        <YearDropdown />
      </div>

      <div className='flex items-center gap-1'>
        <Button
          slot='previous'
          className={cheveronStyle}
        >
          <ChevronLeftIcon size={16} />
        </Button>

        <Button
          slot='next'
          className={cheveronStyle}
        >
          <ChevronRightIcon size={16} />
        </Button>
      </div>
    </header>
  );
}

function CalendarActions({
  confirmLabel = 'Ok',
  cancelLabel = 'Cancel',
  confirmDisabled,
  onConfirm,
  onCancel,
}: Omit<CalendarActionsProps, 'showActions'>) {
  return (
    <div
      data-slot='calendar-actions'
      className='mt-3 flex w-full items-center gap-2'
    >
      <DesignButton
        type='button'
        size='sm'
        className='h-9 flex-1 rounded-xl px-4 text-sm'
        disabled={confirmDisabled}
        onClick={onConfirm}
      >
        {confirmLabel}
      </DesignButton>
      <DesignButton
        type='button'
        size='sm'
        variant='primaryOutline'
        className='h-9 flex-1 rounded-xl px-4 text-sm'
        onClick={onCancel}
      >
        {cancelLabel}
      </DesignButton>
    </div>
  );
}

function CalendarGridComponent({
  isRange = false,
  closeOnSelect = false,
}: {
  isRange?: boolean;
  closeOnSelect?: boolean;
}) {
  const now = today(getLocalTimeZone());

  return (
    <CalendarGridRac>
      <CalendarGridHeaderRac>
        {(day) => (
          <CalendarHeaderCellRac className='text-gray-900/80 size-9 rounded-md p-0 text-xs font-medium'>
            {day}
          </CalendarHeaderCellRac>
        )}
      </CalendarGridHeaderRac>
      <CalendarGridBodyRac className='[&_td]:px-0 [&_td]:py-px'>
        {(date) => (
          <PopoverClose
            onClick={(e) => {
              if (isRange || !closeOnSelect) {
                e.preventDefault();
              }
            }}
            asChild
          >
            <CalendarCellRac
              date={date}
              className={cn(
                'text-gray-900 data-hovered:bg-primary-100 cursor-pointer data-selected:bg-primary-800 data-hovered:text-foreground data-selected:text-white data-focus-visible:ring-ring/50 relative flex size-9 items-center justify-center rounded-md p-0 text-sm font-normal whitespace-nowrap [transition-property:color,background-color,border-radius,box-shadow] duration-150 outline-none data-disabled:pointer-events-none data-disabled:opacity-30 data-focus-visible:z-10 data-focus-visible:ring-[3px] data-unavailable:pointer-events-none data-unavailable:line-through data-unavailable:opacity-30',
                // Range-specific styles
                isRange &&
                  'data-selected:bg-gray-100 data-selected:text-primary-1000 data-invalid:data-selection-end:bg-destructive data-invalid:data-selection-start:bg-destructive data-selection-end:bg-primary-800 data-selection-start:bg-primary-800 data-selection-end:text-white data-selection-start:text-white data-invalid:bg-red-100 data-selected:rounded-none data-selection-end:rounded-e-md data-invalid:data-selection-end:text-white data-selection-start:rounded-s-md data-invalid:data-selection-start:text-white',
                // Today indicator styles
                date.compare(now) === 0 &&
                  cn(
                    'after:bg-gray-900 after:pointer-events-none after:absolute after:start-1/2 after:bottom-1 after:z-10 after:size-[3px] after:-translate-x-1/2 after:rounded-full',
                    isRange
                      ? 'data-selection-end:after:bg-white data-selection-start:after:bg-white'
                      : 'data-selected:after:bg-white'
                  )
              )}
            />
          </PopoverClose>
        )}
      </CalendarGridBodyRac>
    </CalendarGridRac>
  );
}

function Calendar({
  className,
  closeOnSelect,
  showActions = false,
  confirmLabel,
  cancelLabel,
  confirmDisabled,
  onConfirm,
  onCancel,
  ...props
}: DesignCalendarProps) {
  return (
    <CalendarRac
      {...props}
      className={composeRenderProps(className, (className) =>
        cn('w-fit bg-white', className)
      )}
    >
      <CalendarHeader />
      <CalendarGridComponent closeOnSelect={closeOnSelect} />
      {showActions && (
        <CalendarActions
          confirmLabel={confirmLabel}
          cancelLabel={cancelLabel}
          confirmDisabled={confirmDisabled}
          onConfirm={onConfirm}
          onCancel={onCancel}
        />
      )}
    </CalendarRac>
  );
}

function RangeCalendar({
  className,
  closeOnSelect,
  showActions = false,
  confirmLabel,
  cancelLabel,
  confirmDisabled,
  onConfirm,
  onCancel,
  ...props
}: DesignRangeCalendarProps) {
  return (
    <RangeCalendarRac
      {...props}
      className={composeRenderProps(className, (className) =>
        cn('w-fit', className)
      )}
    >
      <CalendarHeader />
      <CalendarGridComponent
        isRange
        closeOnSelect={closeOnSelect}
      />
      {showActions && (
        <CalendarActions
          confirmLabel={confirmLabel}
          cancelLabel={cancelLabel}
          confirmDisabled={confirmDisabled}
          onConfirm={onConfirm}
          onCancel={onCancel}
        />
      )}
    </RangeCalendarRac>
  );
}

export { Calendar, RangeCalendar };
